import { useEffect, useRef } from 'react';
import type { DriveFile, DriveFolder } from '../types';

interface MenuItem {
  icon: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
  divider?: boolean;
}

interface Props {
  x: number;
  y: number;
  item: DriveFile | DriveFolder;
  type: 'file' | 'folder';
  onClose: () => void;
  onDownload?: () => void;
  onRename?: () => void;
  onMove?: () => void;
  onDelete: () => void;
  onDownloadZip?: () => void;
  onShare?: () => void;
  onNewFolder?: () => void;
  selectedCount?: number;
}

export default function ContextMenu({
  x, y, item, type, onClose,
  onDownload, onRename, onMove, onDelete, onDownloadZip, onShare, onNewFolder, selectedCount,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  // Calculate menu height estimate (header + divider + items)
  const itemCount = [
    type === 'file' && onDownload,
    type === 'folder' && onDownloadZip,
    type === 'file' && onShare,
    type === 'folder' && onNewFolder,
    onRename,
    onMove,
    true, // delete always present
  ].filter(Boolean).length;
  const estimatedHeight = 60 + (itemCount * 36) + 10; // header + items + padding

  // Adjust position to stay in viewport
  const adjustedX = Math.min(x, window.innerWidth - 200);
  // If menu would overflow bottom, position it above the cursor
  const adjustedY = (y + estimatedHeight > window.innerHeight) 
    ? Math.max(10, y - estimatedHeight)
    : y;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escHandler);
    };
  }, [onClose]);

  const items: MenuItem[] = [
    ...(type === 'file' && onDownload ? [{
      icon: 'bi-download', 
      label: selectedCount && selectedCount > 1 ? `Descargar (${selectedCount})` : 'Descargar', 
      onClick: () => { onDownload(); onClose(); },
    }] : []),
    ...(type === 'folder' && onDownloadZip ? [{
      icon: 'bi-file-zip', label: 'Descargar como ZIP', onClick: () => { onDownloadZip(); onClose(); },
    }] : []),
    ...(type === 'file' && onShare ? [{
      icon: 'bi-share', label: 'Compartir enlace', onClick: () => { onShare(); onClose(); },
    }] : []),
    ...(type === 'folder' && onNewFolder ? [{
      icon: 'bi-folder-plus', label: 'Nueva carpeta', onClick: () => { onNewFolder(); onClose(); },
    }] : []),
    ...(onRename ? [{
      icon: 'bi-pencil', label: 'Renombrar', onClick: () => { onRename(); onClose(); },
    }] : []),
    ...(onMove ? [{
      icon: 'bi-folder-symlink', label: 'Mover', onClick: () => { onMove(); }, divider: true,
    }] : []),
    {
      icon: 'bi-trash', label: 'Eliminar', danger: true,
      onClick: () => { onDelete(); onClose(); },
      divider: !onMove,
    },
  ];

  return (
    <div
      ref={ref}
      className="dropdown-menu show feebbo-dropdown shadow"
      style={{ position: 'fixed', top: adjustedY, left: adjustedX, minWidth: 180, zIndex: 1055 }}
    >
      <div className="dropdown-header small text-truncate" style={{ maxWidth: 170 }}>
        <i className={`bi ${type === 'folder' ? 'bi-folder-fill text-warning' : 'bi-file-earmark'} me-2`} />
        {selectedCount && selectedCount > 1 ? `${selectedCount} archivos` : item.name}
      </div>
      <div className="dropdown-divider" />

      {items.map((mi, i) => (
        <span key={i}>
          {mi.divider && i > 0 && <div className="dropdown-divider" />}
          <button
            className={`dropdown-item d-flex align-items-center gap-2 ${mi.danger ? 'text-danger' : ''}`}
            onClick={mi.onClick}
          >
            <i className={`bi ${mi.icon}`} />
            {mi.label}
          </button>
        </span>
      ))}
    </div>
  );
}
